import React, { memo } from 'react';
import { CTAButton } from '../components/CTAButton';
import { HighlightBox } from '../components/HighlightBox';
import { Check, X, AlertCircle, Clock, DollarSign, ShieldCheck, Quote } from 'lucide-react';
export function AdvertorialPage() {
  return <div className="min-h-screen bg-[#faf8f3] text-[#2c2c2c] font-serif selection:bg-[#4a7c59] selection:text-white" data-id="element-9">
      {/* Top Bar (Simulated Editorial Header) */}
      <div className="border-b border-[#e0ddd5] bg-white py-3 mb-8" data-id="element-10">
        <div className="max-w-[700px] mx-auto px-5 flex justify-between items-center text-xs uppercase tracking-widest text-gray-500 font-sans" data-id="element-11">
          <span data-id="element-12">Health & Wellness</span>
          <span data-id="element-13">Advertorial</span>
        </div>
      </div>

      <article className="max-w-[700px] mx-auto px-5 pb-20" data-id="element-14">
        {/* Header Section */}
        <header className="mb-10" data-id="element-15">
          <h1 className="text-3xl md:text-4xl lg:text-[40px] font-bold font-sans leading-[1.2] text-gray-900 mb-6" data-id="element-16">
            The 2:43 AM Moment That Changed Everything About How I Think About
            Foot Pain
          </h1>

          <div className="flex items-center gap-4 border-t border-b border-[#e0ddd5] py-4" data-id="element-17">
            <div className="w-12 h-12 bg-gray-200 rounded-full overflow-hidden flex-shrink-0" data-id="element-18">
              <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=100&q=80" alt="Elena Rivera" className="w-full h-full object-cover" data-id="element-19" />
            </div>
            <div className="flex flex-col font-sans" data-id="element-20">
              <span className="font-bold text-gray-900 text-sm" data-id="element-21">
                By Elena Rivera
              </span>
              <span className="text-gray-500 text-xs uppercase tracking-wide" data-id="element-22">
                Restaurant Manager • 4 min read
              </span>
            </div>
          </div>
        </header>

        {/* Hero Image - Image 1 */}
        <figure className="mb-10" data-id="element-23">
          <img src="https://cdn.magicpatterns.com/uploads/kUtvawoUQkzFptMUPmtp2r/1_изображение(стелька).png" alt="ReLive HexaCore insole product" className="w-full h-auto rounded-sm shadow-sm mb-3" data-id="element-24" />
          <figcaption className="text-sm text-gray-500 italic text-center font-sans" data-id="element-25">
            "I stood there in the dark... literally unable to put weight on my
            left foot without wincing."
          </figcaption>
        </figure>

        {/* Main Content */}
        <div className="prose prose-lg prose-stone max-w-none font-serif leading-[1.9] text-[#2c2c2c]" data-id="element-26">
          <p className="first-letter:text-5xl first-letter:font-bold first-letter:mr-2 first-letter:float-left first-letter:leading-[0.8] first-letter:text-[#4a7c59]" data-id="element-27">
            It was 2:43 AM on a Tuesday when my feet woke me up. Not a gentle
            nudge. Not a dull ache. A searing, stabbing pain that shot from my
            heels straight up through my calves the moment I tried to stand up
            to use the bathroom.
          </p>

          <p data-id="element-28">
            I stood there in the dark, one hand gripping the nightstand,
            literally unable to put weight on my left foot without wincing. And
            that's when I realized:{' '}
            <strong data-id="element-29">
              I'm 41 years old and I can't walk to my own bathroom without pain.
            </strong>
          </p>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-30">
            I Did Everything Right
          </h2>

          <p data-id="element-31">
            Let me back up. I'm a restaurant manager. I'm on my feet 10-12 hours
            a day, six days a week. Concrete floors. Constant movement. I
            accepted years ago that my feet would be "tired" by closing time.
          </p>

          <p data-id="element-32">
            But over the past year, "tired" turned into something else entirely.
            By hour 6 of my shift, my heels felt like they were on fire. By hour
            9, I was limping between tables. By the time I got home, I couldn't
            stand long enough to take a shower without sitting down halfway
            through.
          </p>

          <p data-id="element-33">
            So I did what everyone does: I tried to fix it. Here's what I
            bought:
          </p>

          <div className="my-8 space-y-4 bg-white p-6 rounded-lg border border-[#e0ddd5]" data-id="element-34">
            <ul className="space-y-4 list-none pl-0" data-id="element-35">
              <li className="flex gap-3" data-id="element-36">
                <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-37" />
                <div data-id="element-38">
                  <strong className="font-sans block text-gray-900" data-id="element-39">
                    The $15 gel insoles from CVS
                  </strong>
                  <span className="text-gray-600" data-id="element-40">
                    Felt like heaven for exactly 4 days. Then they flattened
                    into useless rubber pancakes. Bought them three more times
                    thinking I got a "bad batch." Same result. (Cost: $60)
                  </span>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-41">
                <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-42" />
                <div data-id="element-43">
                  <strong className="font-sans block text-gray-900" data-id="element-44">
                    The $45 memory foam inserts
                  </strong>
                  <span className="text-gray-600" data-id="element-45">
                    The package promised "custom molding to your unique foot
                    shape." They molded alright. They molded into the exact
                    shape of my collapsed arches. Within two weeks, walking on
                    them felt like walking on compressed cardboard.
                  </span>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-46">
                <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-47" />
                <div data-id="element-48">
                  <strong className="font-sans block text-gray-900" data-id="element-49">
                    The $90 "orthopedic-grade" rigid inserts
                  </strong>
                  <span className="text-gray-600" data-id="element-50">
                    These were supposed to be "medical quality." They felt like
                    I'd duct-taped blocks of wood to my feet. I lasted 3 hours
                    before I ripped them out and threw them in my car trunk
                    where they still sit today.
                  </span>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-51">
                <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-52" />
                <div data-id="element-53">
                  <strong className="font-sans block text-gray-900" data-id="element-54">
                    The $600 custom orthotics from a podiatrist
                  </strong>
                  <span className="text-gray-600" data-id="element-55">
                    I was desperate. I paid for the foot scan, the custom mold,
                    the works. They took 3 weeks to arrive. They helped... for
                    about a month. Then my feet adjusted and the pain came right
                    back.
                  </span>
                </div>
              </li>
            </ul>
            <div className="mt-6 pt-4 border-t border-gray-100 text-center font-bold text-lg text-red-800 font-sans" data-id="element-56">
              Total spent: $795. Total relief: Zero.
            </div>
          </div>

          <p data-id="element-57">
            That 2:43 AM moment—standing in my bedroom unable to walk—was my
            breaking point.
          </p>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-58">
            The Question That Changed Everything
          </h2>

          <p data-id="element-59">
            The next morning, frustrated and exhausted, I did something
            different. Instead of searching "best insoles for foot pain," I
            searched: <strong data-id="element-60">"Why do insoles stop working?"</strong>
          </p>

          <p data-id="element-61">
            That's when I found an article written by a biomechanics researcher
            that made my blood boil. He explained something the insole industry
            desperately doesn't want you to understand:{' '}
            <strong data-id="element-62">
              The softer your insole feels in the store, the faster it will
              destroy your feet.
            </strong>
          </p>

          <p data-id="element-63">Wait, what? He explained it like this:</p>

          <blockquote className="border-l-4 border-[#4a7c59] pl-6 py-2 my-8 italic text-xl text-gray-700 bg-white pr-4 rounded-r-lg" data-id="element-64">
            "Imagine trying to build a house on a waterbed. It feels soft and
            comfortable at first. But the moment you put actual weight on it,
            everything sinks and collapses. That's what gel and memory foam
            insoles do to your arches."
          </blockquote>

          <p className="font-bold" data-id="element-65">Holy shit.</p>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-66">
            The Cushioning Trap (And Why I Fell For It)
          </h2>

          <p data-id="element-67">
            Here's what actually happens when you stand on soft insoles: Your
            heel presses down. The gel spreads. Your arch—which is already under
            pressure from holding up your body weight—starts to collapse inward.
          </p>

          {/* Body Section First Insert - Image 2 */}
          <figure className="my-8" data-id="element-68">
            <img src="https://cdn.magicpatterns.com/uploads/fovM3UAjL297qX5V1tfhwF/2_изображение(стелька).png" alt="ReLive HexaCore insole showing support structure" className="w-full h-auto rounded-sm shadow-sm mb-3" data-id="element-69" />
            <figcaption className="text-sm text-gray-500 italic text-center font-sans" data-id="element-70">
              Soft insoles mold to your collapsed arch instead of supporting it.
            </figcaption>
          </figure>

          <p data-id="element-71">
            And here's the sick part: The gel follows that collapse. It doesn't
            hold your arch up. It molds to your collapsed arch. So within days,
            you're not standing on "support." You're standing on a thin,
            compressed layer of gel that's shaped exactly like your tired,
            falling foot.
          </p>

          <p data-id="element-72">
            The memory foam does the same thing. It "conforms to your unique
            foot shape"—but if your foot shape is collapsing under pressure, it
            just makes the problem worse. I felt like an idiot. I'd spent nearly
            $900 trying to solve my foot pain with the exact thing that was
            causing it.
          </p>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-73">
            What Your Feet Actually Need
          </h2>

          <p data-id="element-74">
            The article explained something that should be obvious but somehow
            isn't: Your foot is not a flat surface. It's three arches working
            together.
          </p>

          <p data-id="element-75">
            When those arches are properly supported, your weight distributes
            evenly. Pressure spreads out. Your heel, your midfoot, your
            forefoot—they all share the load. But when those arches collapse?
            Pressure concentrates in just a few spots. Your heel takes a
            beating. The ball of your foot burns. Your ankle rolls. Your knee
            compensates.
          </p>

          <p data-id="element-76">
            By the end of a 10-hour shift, you're not just tired—you're
            structurally breaking down. Cushioning doesn't fix this. It can't.
            Soft materials compress under weight. That's literally what makes
            them soft. What you actually need is something that holds your foot
            in proper alignment while still absorbing shock. Not soft. Not
            rigid.{' '}
            <strong data-id="element-77">Structured support with intelligent cushioning.</strong>
          </p>

          <HighlightBox title="The Three Points That Changed My Life" icon={<ShieldCheck className="w-6 h-6" data-id="element-79" />} data-id="element-78">
            <p className="mb-4" data-id="element-80">
              Real support—the kind that actually works—requires three specific
              points:
            </p>

            <div className="space-y-6" data-id="element-81">
              <div data-id="element-82">
                <strong className="font-sans text-lg block text-[#4a7c59] mb-1" data-id="element-83">
                  The Heel
                </strong>
                <p className="m-0" data-id="element-84">
                  Your heel needs to be cupped and stabilized. A deep heel cup
                  centers your heel so the impact travels straight up instead of
                  twisting your ankle sideways.
                </p>
              </div>

              <div data-id="element-85">
                <strong className="font-sans text-lg block text-[#4a7c59] mb-1" data-id="element-86">
                  The Medial Arch (Inside Edge)
                </strong>
                <p className="m-0" data-id="element-87">
                  This is the arch everyone talks about, but most insoles get it
                  wrong. You don't need a hard bump. You need continuous support
                  along the entire arch—firm enough to prevent collapse, but
                  gradual enough that it doesn't feel like a rock.
                </p>
              </div>

              <div data-id="element-88">
                <strong className="font-sans text-lg block text-[#4a7c59] mb-1" data-id="element-89">
                  The Lateral Arch (Outside Edge)
                </strong>
                <p className="m-0" data-id="element-90">
                  This is the one nobody mentions. When the outside edge of your
                  foot collapses, your toes splay and pressure concentrates
                  under the ball of your foot (that burning sensation by hour
                  8). Proper lateral support stops this.
                </p>
              </div>
            </div>

            <p className="mt-6 mb-0" data-id="element-91">
              When all three points work together—the collapse stops. The pain
              fades.
            </p>
          </HighlightBox>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-92">
            Then I Found The Answer in Aircraft Engineering
          </h2>

          <p data-id="element-93">
            The breakthrough came from an unexpected place: honeycomb
            structures. Not the kind bees make. The engineering kind. This
            structure is used in aircraft and race cars because it's incredibly
            strong for how light it is. It distributes force evenly across the
            entire surface while maintaining its shape under massive pressure.
          </p>

          {/* Body Section Second Insert - Image 3 */}
          <figure className="my-8" data-id="element-94">
            <img src="https://cdn.magicpatterns.com/uploads/4ePR2pQE21MkXQteL3nghi/3_изображение(стелька).png" alt="ReLive HexaCore honeycomb technology" className="w-full h-auto rounded-sm shadow-sm mb-3" data-id="element-95" />
            <figcaption className="text-sm text-gray-500 italic text-center font-sans" data-id="element-96">
              Honeycomb structures distribute force evenly while maintaining
              shape under pressure.
            </figcaption>
          </figure>

          <p data-id="element-97">
            Instead of a solid layer of gel or foam that compresses and stays
            compressed, honeycomb cells compress slightly to absorb shock, then
            bounce back. You get shock absorption—that cushioned feeling—without
            the structural failure.
          </p>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-98">
            Finding The "Hidden Gem" Brand
          </h2>

          <p data-id="element-99">
            I started looking for insoles that used this specific honeycomb
            technology combined with the 3-point support. Most of them were big
            "medical brands" charging $150 or more for a single pair. I wasn't
            going to pay that.
          </p>

          <p data-id="element-100">
            I almost gave up, but then I found a smaller, direct-to-consumer
            company called ReLive. They were using the exact same
            aerospace-grade honeycomb material and the 3-point support system,
            but because they don't have physical stores or expensive TV ads,
            they were selling them for a fraction of the price.
          </p>

          <p data-id="element-101">Here's what made them stand out to me:</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8" data-id="element-102">
            <div className="bg-white p-5 rounded-lg border border-[#e0ddd5]" data-id="element-103">
              <h4 className="font-sans font-bold text-[#4a7c59] mb-2" data-id="element-104">
                The Base Layer
              </h4>
              <p className="text-sm m-0" data-id="element-105">
                A firm, contoured shell that cups the heel and supports both
                arches. It holds the foot in alignment without feeling like a
                plank of wood.
              </p>
            </div>
            <div className="bg-white p-5 rounded-lg border border-[#e0ddd5]" data-id="element-106">
              <h4 className="font-sans font-bold text-[#4a7c59] mb-2" data-id="element-107">
                The Honeycomb Core
              </h4>
              <p className="text-sm m-0" data-id="element-108">
                Thousands of tiny hexagonal cells that absorb shock without
                flattening.
              </p>
            </div>
            <div className="bg-white p-5 rounded-lg border border-[#e0ddd5] md:col-span-2" data-id="element-109">
              <h4 className="font-sans font-bold text-[#4a7c59] mb-2" data-id="element-110">
                The Fit
              </h4>
              <p className="text-sm m-0" data-id="element-111">
                They fit perfectly in my non-slip work shoes, my sneakers, and
                even my casual flats and boots.
              </p>
            </div>
          </div>

          {/* CTA Button - Inserted after "casual flats and boots" */}
          <div className="my-10 flex justify-center" data-id="element-112">
            <CTAButton text="View The Honeycomb Technology →" className="max-w-md" data-id="element-113" />
          </div>

          {/* Footer/Conclusion Section - Image 4 */}
          <figure className="my-8" data-id="element-114">
            <img src="https://cdn.magicpatterns.com/uploads/ksinaaHtC95rrr15kSbtXG/4_Изображение(стелька).png" alt="ReLive HexaCore insole layers exploded view" className="w-full h-auto rounded-sm shadow-sm mb-3" data-id="element-115" />
            <figcaption className="text-sm text-gray-500 italic text-center font-sans" data-id="element-116">
              The ReLive HexaCore insole with advanced honeycomb technology and
              multi-layer support system.
            </figcaption>
          </figure>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-117">
            My Results (The Truth)
          </h2>

          <p data-id="element-118">
            I decided to give them a shot because they offered a guarantee. Here
            is my honest timeline:
          </p>

          <HighlightBox title="My Timeline" icon={<Clock className="w-6 h-6" data-id="element-120" />} data-id="element-119">
            <div className="space-y-4" data-id="element-121">
              <div className="flex gap-4" data-id="element-122">
                <div className="font-bold font-sans text-[#4a7c59] w-20 flex-shrink-0" data-id="element-123">
                  Day 1
                </div>
                <div data-id="element-124">"These feel… different. Firm but not hard."</div>
              </div>
              <div className="flex gap-4" data-id="element-125">
                <div className="font-bold font-sans text-[#4a7c59] w-20 flex-shrink-0" data-id="element-126">
                  Day 3
                </div>
                <div data-id="element-127">"Wait, my feet don't hurt at closing time."</div>
              </div>
              <div className="flex gap-4" data-id="element-128">
                <div className="font-bold font-sans text-[#4a7c59] w-20 flex-shrink-0" data-id="element-129">
                  Day 7
                </div>
                <div data-id="element-130">"I'm not limping anymore."</div>
              </div>
              <div className="flex gap-4" data-id="element-131">
                <div className="font-bold font-sans text-[#4a7c59] w-20 flex-shrink-0" data-id="element-132">
                  Day 30
                </div>
                <div data-id="element-133">"I'm not sending these back."</div>
              </div>
              <div className="flex gap-4" data-id="element-134">
                <div className="font-bold font-sans text-[#4a7c59] w-20 flex-shrink-0" data-id="element-135">
                  Month 8
                </div>
                <div data-id="element-136">"I'm buying a second pair as a backup."</div>
              </div>
            </div>
          </HighlightBox>

          <HighlightBox title="The Math That Made Me Angry" icon={<DollarSign className="w-6 h-6" data-id="element-138" />} data-id="element-137">
            <p data-id="element-139">
              Here's what really pisses me off about the whole thing: I spent
              $795 on insoles that didn't work. These ReLive HexaCore insoles
              cost me $49.95. If I'd found them first, I would have saved $745
              and 11 months of daily pain.
            </p>

            <p className="font-bold mt-4 mb-2" data-id="element-140">But let's go deeper.</p>

            <ul className="space-y-3 list-none pl-0" data-id="element-141">
              <li className="flex justify-between items-center border-b border-gray-100 pb-2" data-id="element-142">
                <span data-id="element-143">
                  Extra shoes bought because of uneven wear from limping
                </span>
                <span className="font-bold text-red-700" data-id="element-144">$240</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-100 pb-2" data-id="element-145">
                <span data-id="element-146">Chiropractor visit because my back was compensating</span>
                <span className="font-bold text-red-700" data-id="element-147">$180</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-100 pb-2" data-id="element-148">
                <span data-id="element-149">Lost wages (called in sick 3 times)</span>
                <span className="font-bold text-red-700" data-id="element-150">$600</span>
              </li>
            </ul>

            <div className="mt-4 pt-2 flex justify-between items-center font-bold text-lg" data-id="element-151">
              <span data-id="element-152">Total cost of ignoring the real problem:</span>
              <span className="text-red-700" data-id="element-153">$1,815</span>
            </div>
            <div className="mt-2 flex justify-between items-center font-bold text-lg" data-id="element-154">
              <span data-id="element-155">Cost of fixing it:</span>
              <span className="text-[#4a7c59]" data-id="element-156">$49.95</span>
            </div>
          </HighlightBox>

          <h2 className="text-2xl font-bold font-sans mt-10 mb-4 text-gray-900" data-id="element-157">
            The Part They Don't Tell You
          </h2>

          <p data-id="element-158">
            Here's something else nobody mentions: When your feet stop hurting,
            everything else stops hurting too. My knees don't ache anymore. My
            lower back doesn't tighten up by evening. I don't wake up at 2:43 AM
            unable to walk to the bathroom.
          </p>

          <p className="mb-8" data-id="element-159">
            <strong data-id="element-160">Fixing your feet fixes everything above them.</strong>
          </p>

          <div className="bg-[#fff5e6] border-l-4 border-[#ff9500] rounded-r-lg p-6 my-10" data-id="element-161">
            <h3 className="text-xl font-bold font-sans mb-4 text-[#2c2c2c] flex items-center gap-2" data-id="element-162">
              <AlertCircle className="w-6 h-6 text-[#ff9500]" data-id="element-163" />A Quick Update
              for Readers (Wednesday, December 18th)
            </h3>

            <p className="mb-4" data-id="element-164">
              Since posting this story, my inbox has been flooded with people
              asking for the link.
            </p>

            <p className="mb-4" data-id="element-165">
              I originally bought mine during their Black Friday Sale a few
              weeks ago using a special promo code.
            </p>

            <p className="mb-4" data-id="element-166">
              I just went to their site to check if the sale was over, and I
              realized something crazy:
            </p>

            <p className="mb-4 font-bold" data-id="element-167">
              The discount code{' '}
              <span className="bg-[#4a7c59] text-white px-2 py-1 rounded font-mono" data-id="element-168">
                BlackFriday2025
              </span>{' '}
              is STILL working.
            </p>

            <p className="mb-4" data-id="element-169">
              I think their web team might have forgotten to disable it after
              the sale ended.
            </p>

            <p className="mb-4" data-id="element-170">
              When you enter <span className="font-bold" data-id="element-171">BlackFriday2025</span>{' '}
              at checkout, it still applies the "Buy 1 Get 1 Free" offer
              (basically giving you the second pair for free).
            </p>

            <p className="mb-6" data-id="element-172">
              I don't know if this is a glitch or just an oversight, but if you
              enter the code <span className="font-bold" data-id="element-173">BlackFriday2025</span>{' '}
              and it works, grab it immediately before they realize the mistake
              and fix it.
            </p>

            <CTAButton text="Apply Code BlackFriday2025 Here →" className="shadow-lg" data-id="element-174" />

            <p className="mt-4 text-sm text-gray-600 italic mb-0" data-id="element-175">
              Note: If the code says "Expired" at checkout, then they probably
              finally fixed the glitch. Sorry!
            </p>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#2c2c2c] text-gray-400 py-12 text-center text-sm font-sans" data-id="element-176">
        <div className="max-w-[700px] mx-auto px-5" data-id="element-177">
          <p className="mb-4" data-id="element-178">© 2024 ReLive HexaCore. All rights reserved.</p>
          <p data-id="element-179">
            This is an advertisement and not an actual news article, blog, or
            consumer protection update.
          </p>
        </div>
      </footer>
    </div>;
}